import type { InputHTMLAttributes } from 'react'
import type { FieldValues, Path, Control } from 'react-hook-form'

import { Checkbox } from 'src/shared/components/ui/checkbox'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from 'src/shared/components/ui/forms/form'
import { cn } from 'src/shared/utils/cn'

interface CheckboxFormFieldProps<T extends FieldValues> extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  name: Path<T>
  label: string
  description?: string
  className?: string
  required?: boolean
  control?: Control<T>
  reverse?: boolean // Label on the left
  labelClassName?: string
  checkboxClassName?: string
}

export function CheckboxFormField<T extends FieldValues>({
  name,
  label,
  description,
  className,
  disabled,
  required,
  control,
  reverse = false,
  labelClassName,
  checkboxClassName,
}: CheckboxFormFieldProps<T>) {
  if (required) {
    label = `${label} *`
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem
          className={cn('flex flex-row items-center space-x-3 space-y-1', reverse && 'justify-between', className)}
        >
          {reverse && <FormLabel className={cn('mt-0.5', labelClassName)}>{label}</FormLabel>}
          <FormControl>
            <Checkbox
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={disabled}
              className={cn('mt-1 h-7 w-7', checkboxClassName)}
            />
          </FormControl>
          {!reverse && <FormLabel className={cn('mt-0.5', labelClassName)}>{label}</FormLabel>}
          {description && <FormDescription className="mt-0.5">{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
